import * as RadixDialog from '@radix-ui/react-dialog';
import { forwardRef, useCallback, useEffect, useRef } from 'react';
import { type ButtonAction, ButtonActions, Icon, IconBadge, Link, Markdown } from '~/components/granular';
import { classNames } from '~/utils/styles';
import type { DialogProps } from './Dialog.types';

export const Dialog = forwardRef<HTMLDivElement, DialogProps>(
  (
    {
      actions,
      actionsPrefix,
      children,
      className = '',
      contentClassName,
      icon,
      iconAppearance,
      linkAction,
      markdown,
      open,
      preventOutsideClickClose,
      side,
      title,
      trigger,
      width = 'medium',
      withContentScroller = true,
      onEscapeKeyDown,
      onCloseBtnClick,
      onOpenChange,
      ...props
    },
    forwardedRef,
  ) => {
    const contentRef = useRef<HTMLDivElement | null>(null);
    const wasStackedOnPointerDownRef = useRef(false);

    const setContentRef = useCallback(
      (node: HTMLDivElement | null) => {
        contentRef.current = node;

        if (typeof forwardedRef === 'function') {
          forwardedRef(node);
          return;
        }

        if (forwardedRef) {
          forwardedRef.current = node;
        }
      },
      [forwardedRef],
    );

    // Radix defers reporting an outside pointer down to the `click` that follows it, by which time a
    // dropdown opened inside the dialog has already been dismissed by that same pointer down — so the
    // dialog sees a plain outside click and closes too. Radix marks the dialog layer `pointer-events:
    // none` while another layer sits on top of it, so record that at pointer down time and keep the
    // dialog open when the interaction only dismissed the layer above it.
    useEffect(() => {
      function handlePointerDown() {
        wasStackedOnPointerDownRef.current = contentRef.current?.style.pointerEvents === 'none';
      }

      document.addEventListener('pointerdown', handlePointerDown, true);
      return () => document.removeEventListener('pointerdown', handlePointerDown, true);
    }, []);

    return (
      <RadixDialog.Root onOpenChange={onOpenChange} open={open}>
        {trigger ? <RadixDialog.Trigger asChild>{trigger}</RadixDialog.Trigger> : null}
        <RadixDialog.Portal>
          <RadixDialog.Overlay
            className={classNames('fixed inset-0 z-20 flex bg-black/50', {
              'data-[state=open]:animate-overlayShow': !side,
              'data-[state=open]:animate-slideLeftAndFade': side === 'right',
              'data-[state=open]:animate-slideRightAndFade': side === 'left',
            })}
          >
            <RadixDialog.Content
              aria-describedby={undefined}
              className={classNames(
                'm-auto flex max-h-[85vh] w-full flex-col overflow-hidden rounded-[6px] bg-white shadow-xl focus:outline-hidden',
                {
                  // side opening
                  'data-[state=open]:animate-contentShow': !side,
                  'max-w-[340px]': width === 'small' && !side,
                  'max-w-[480px]': width === 'medium' && !side,
                  'max-w-[920px]': width === 'large' && !side,
                  'max-w-[calc(100%-10rem)]': width === 'full',
                  'ml-0 data-[state=open]:animate-slideRightAndFade': side === 'left',
                  'mr-0 data-[state=open]:animate-slideLeftAndFade': side === 'right',
                  'mt-0 h-screen max-h-screen max-w-[650px] rounded-none': side === 'left' || side === 'right',
                },
                className,
              )}
              onEscapeKeyDown={() => {
                onEscapeKeyDown?.();
              }}
              onPointerDownOutside={(event) => {
                if (preventOutsideClickClose || wasStackedOnPointerDownRef.current) {
                  event.preventDefault();
                }
              }}
              ref={setContentRef}
              {...props}
            >
              <div
                className={classNames('flex items-center border-solid px-6 py-4', {
                  'border-b border-neutral-200': !side,
                })}
              >
                {icon && <IconBadge appearance={iconAppearance} className="mr-3" icon={icon} size="small" />}
                <RadixDialog.Title className="title-200 text-neutral-1000" data-testid="dialog-heading">
                  {title}
                </RadixDialog.Title>
                <RadixDialog.Close asChild className="ml-auto">
                  <button aria-label="Close" onClick={() => onCloseBtnClick?.()} type="button">
                    <Icon className="text-neutral-1000" height={24} icon="CloseIcon" width={24} />
                  </button>
                </RadixDialog.Close>
              </div>
              {withContentScroller ? (
                <DialogContent
                  contentClassName={contentClassName}
                  side={side}
                  markdown={markdown}
                  actions={actions}
                  actionsPrefix={actionsPrefix}
                  linkAction={linkAction}
                >
                  {children}
                </DialogContent>
              ) : (
                children
              )}
            </RadixDialog.Content>
          </RadixDialog.Overlay>
        </RadixDialog.Portal>
      </RadixDialog.Root>
    );
  },
);

function DialogContent({
  children,
  contentClassName,
  side,
  markdown,
  actions,
  actionsPrefix,
  linkAction,
}: Pick<
  DialogProps,
  'children' | 'contentClassName' | 'side' | 'markdown' | 'actions' | 'actionsPrefix' | 'linkAction'
>) {
  return (
    <div className={classNames('min-h-0 flex-1 overflow-y-auto', { 'flex flex-col': !!side })}>
      <div
        className={classNames('body-200-light px-6', contentClassName, {
          'grow pb-8': !!side,
          'h-full py-5': !side,
        })}
      >
        {markdown && <Markdown markdown={markdown} />}
        {children}
      </div>

      {(actions || linkAction) && (
        <div
          className={classNames('flex items-center justify-end gap-2 px-6 pb-6', {
            'sticky bottom-0 w-full border-t border-t-neutral-300 bg-white pt-5': !!side,
          })}
          data-testid="dialog-actions"
        >
          {actionsPrefix && <div className="mr-auto">{actionsPrefix}</div>}
          {linkAction ? (
            <Link
              href={linkAction.href}
              key={linkAction.id}
              leftIcon={linkAction.leftIcon}
              rightIcon={linkAction.rightIcon}
              target={linkAction.target}
            >
              {linkAction.children}
            </Link>
          ) : (
            <ButtonActions actions={actions as Array<ButtonAction>} />
          )}
        </div>
      )}
    </div>
  );
}
